<template>
  <GhostContent
    v-if="data"
    :data="data"
  >
    <template #header>
      <TContainer
        size="sm"
        class="space-y-6 text-center mb-8"
      >
        <TMarketingPreTitle v-if="tags.length">
          <template
            v-for="(tag, index) in tags"
            :key="tag.slug"
          >
            <NuxtLink
              :to="localePath({ name: 'category-slug', params: { slug: tag.slug } })"
              class="hover:underline"
            >
              {{ tag.name }}
            </NuxtLink>
            <span v-if="index < tags.length - 1">, </span>
          </template>
        </TMarketingPreTitle>

        <THeadingMarketing
          :level="3"
          class="text-center"
        >
          {{ data.title }}
        </THeadingMarketing>

        <p
          v-if="data.published_at"
          class="text-gray-500"
        >
          {{ t('pages.blog.postedOnText') }} {{ formatAsFullDateLong(data.published_at) }}
        </p>
      </TContainer>
    </template>

    <template #footer>
      <TContainer
        size="sm"
        class="mt-12"
      >
        <div class="flex flex-col items-center justify-between gap-y-8">
          <GhostShare
            v-if="data.title"
            :share-text="data.title"
          />
          <GhostAuthor
            v-if="data?.primary_author"
            :author="data.primary_author"
          />
        </div>
      </TContainer>
    </template>
  </GhostContent>
</template>

<script setup lang="ts">
const runtimeConfig = useRuntimeConfig()
const { t } = useI18n()
const localePath = useLocalePath()
const { formatAsFullDateLong } = useDates()
const route = useRoute()
const { getPost } = useGhostApi()
const { rewriteAuthorUrl } = useAugmentedSchemaOrg()

const { data, error } = await useAsyncData(
  computed(() => `post-${route.params.slug}`),
  () => getPost(route.params.slug as string),
)

if (!data.value || error.value?.status) {
  throw createError({
    statusCode: error.value?.status ?? 404,
    statusText: `There was an issue fetching a Ghost Post: (${route.params.slug})`,
  })
}

const tags = computed(() => data.value?.tags?.filter(({ slug, visibility }) => slug && !runtimeConfig.public.locales.includes(slug) && visibility === 'public') ?? [])

const post = data.value
const { primary_author: primaryAuthor } = post
const authorPageUrl = rewriteAuthorUrl(primaryAuthor?.url)
const blogAuthor = primaryAuthor?.name?.includes('RVezy')
  ? defineOrganization({
      '@id': `${runtimeConfig.public.baseURL}/#identity`,
      'url': authorPageUrl,
    })
  : definePerson({
      name: primaryAuthor?.name ?? '',
      image: primaryAuthor?.profile_image,
      url: authorPageUrl,
    })

useSeoMeta({
  title: post.meta_title || post.title,
  description: post.meta_description || post.excerpt,

  ogType: 'article',
  ogTitle: post.og_title || post.meta_title || post.title,
  ogDescription: post.og_description || post.meta_description || post.excerpt,
  ogImage: post.og_image || post.feature_image,

  twitterTitle: post.twitter_title || post.meta_title || post.title,
  twitterDescription: post.twitter_description || post.meta_description || post.excerpt,
  twitterImage: post.twitter_image || post.feature_image,
})

useSchemaOrg([
  defineArticle({
    '@type': 'BlogPosting',
    'headline': post.meta_title || post.title,
    'description': post.meta_description,
    'datePublished': post.published_at,
    'dateModified': post.updated_at,
    'author': blogAuthor,
    'image': defineImage({
      url: post.feature_image,
    }),
  }),
])
</script>

<i18n src="~~/i18n/locales/common/pages/blog.json" lang="json" />
